import { useRef } from "react";
import toast from "react-hot-toast";
import { startCase } from "lodash";

import { BoxedContent } from "@/components/common";
import { DynamicFormContainer } from "@/components/dynamic";
import dynamicObjectMap from "@/utils/maps/dynamicObjectMap";
import { getDynamicObjectRecordById } from "@/api/dynamic/dynamicObjectNameApi";
import { formatDecimalValues, calculateTax, getReversedPaymentAmounts } from "@/utils/helpers";

const calculateTotalAndTax = ({ rentalDetail }) => {
  const data = {
    subtotal: 0,
    taxAmount: 0,
  };

  data.subtotal = rentalDetail.reduce(
    (prevValue, currentValue) => Number(prevValue) + Number(currentValue.amount),
    0
  );

  data.taxAmount = rentalDetail.reduce(
    (prevValue, currentValue) => Number(prevValue) + Number(currentValue.taxAmount),
    0
  );

  data.total = Number(data.subtotal) + Number(data.taxAmount);

  return data;
};

// Scale the line amounts by reversedAmount / totalAmount when the invoice line has been
// reversed by earlier credit notes.
const getRentalDetailAmounts = (payment) =>
  getReversedPaymentAmounts(payment, [
    "rentAmount",
    "utilityAmount",
    "securityDepositAmount",
    "adminChargesAmount",
  ]) || {
    rentAmount: payment.rentAmount,
    utilityAmount: payment.utilityAmount,
    securityDepositAmount: payment.securityDepositAmount,
    adminChargesAmount: payment.adminChargesAmount,
    amount: payment.amount,
    taxAmount: payment.taxAmount,
    totalAmount: payment.totalAmount,
  };

const loadedAmountFields = [
  "amount",
  "tax",
  "taxAmount",
  "totalAmount",
  "rentAmount",
  "utilityAmount",
  "securityDepositAmount",
  "adminChargesAmount",
  "generalInvoiceAmount",
];

// Keep a snapshot of the amounts the line was loaded with, so edits can't credit more than that.
const withLoadedAmounts = (row) => ({
  ...row,
  loadedAmounts: Object.fromEntries(
    loadedAmountFields.filter((field) => field in row).map((field) => [field, row[field]])
  ),
});

const formatRentalInvoiceDetail = (rentalInvoice) => {
  const invoiceRentalDetail = rentalInvoice?.rentalDetail || [];
  const hasReversedAmount = invoiceRentalDetail.some(
    (payment) => Number(payment?.reversedAmount || 0) > 0
  );

  const rentalDetail = invoiceRentalDetail
    .map((payment) => ({
      contract: {
        label: payment?.contract?.number,
        value: payment?.contract?.id,
      },
      building: {
        label: payment?.building?.name,
        value: payment?.building?.id,
      },
      component: {
        label: payment?.contract?.component?.name,
        value: payment?.contract?.component?.id,
      },
      unit: payment?.unit?.map((unit) => ({
        label: unit?.name,
        value: unit?.id,
      })),
      chequeNo: payment.chequeNo || "",
      paymentDate: new Date(payment.paymentDate),
      ...getRentalDetailAmounts(payment),
      paymentMethod: {
        label: startCase(payment.paymentMethod),
        value: payment.paymentMethod,
      },
      tax: {
        label: payment?.tax?.name,
        value: payment?.tax?.id,
        saleRate: payment?.tax?.saleRate,
      },
      description: payment.description,
      paymentDetailId: payment.paymentDetailId,
      invoiceLineItemId: payment.id,
    }))
    .map(withLoadedAmounts)
    .sort((a, b) => b.paymentDate - a.paymentDate);

  if (hasReversedAmount) {
    const { subtotal, taxAmount, total } = calculateTotalAndTax({ rentalDetail });

    return {
      rentalDetail,
      subtotal,
      taxAmount,
      total: formatDecimalValues(total),
    };
  }

  return {
    rentalDetail,
    subtotal: rentalInvoice?.subtotal,
    taxAmount: rentalInvoice?.taxAmount,
    total: formatDecimalValues(rentalInvoice?.total),
  };
};

const formatGeneralInvoiceDetail = (invoice) => {
  const {
    component, lineItem, date, dueDate, subtotal, amountBeforeTax, taxAmount, total
  } = invoice || {};

  const rentalDetail = (lineItem || [])
    .map((item) => ({
      account: {
        label: item?.account?.name,
        value: item?.account?.id,
      },
      component: {
        label: component?.name,
        value: component?.id,
      },
      paymentDate: new Date(dueDate || date),
      amount: item.amount,
      generalInvoiceAmount: item.amount,
      tax: {
        label: item?.tax?.name,
        value: item?.tax?.id,
        saleRate: item?.tax?.saleRate,
      },
      taxAmount: item.taxAmount,
      totalAmount: item.totalAmount,
      description: item.description,
    }))
    .map(withLoadedAmounts);

  return {
    rentalDetail,
    subtotal,
    amountBeforeTax,
    taxAmount,
    total: formatDecimalValues(total || 0),
  };
};

const emptyInvoiceSelection = {
  rentalDetail: [],
  subtotal: null,
  amountBeforeTax: null,
  taxAmount: null,
  total: null,
};

// Credit note line amounts are net of tax, same as the source invoices.
const defaultAmountOfTax = { value: "Exclusive" };

const exceedsLoadedTotal = ({ totalAmount, loadedAmounts }) =>
  loadedAmounts &&
  Number(totalAmount || 0) > Number(loadedAmounts.totalAmount || 0) + 0.005;

function CreditNoteForm() {
  const ref = useRef(null);

  const applyRentalInvoice = async (rentalInvoiceValue) => {
    try {
      const rentalInvoice = await getDynamicObjectRecordById(
        dynamicObjectMap.get("RentalInvoiceObjectName"),
        rentalInvoiceValue.value,
      );

      // Bail out if the user picked a different rental invoice while this was in flight.
      if (ref.current.getState().rentalInvoice?.value !== rentalInvoiceValue.value) {
        return;
      }

      if (rentalInvoice?.status === "Reversed") {
        toast.error("Cannot select a reversed rental invoice");
        ref.current.setFormValue("rentalInvoice", null);
        ref.current.setFormState(emptyInvoiceSelection);
        return;
      }

      const paymentDetailIds = [
        ...new Set(
          (rentalInvoice?.rentalDetail || [])
            .map((detail) => detail.paymentDetailId)
            .filter(Boolean),
        ),
      ];

      const paymentDetails = await Promise.all(
        paymentDetailIds.map((paymentDetailId) =>
          getDynamicObjectRecordById(
            dynamicObjectMap.get("ContractPaymentDetailObjectName"),
            paymentDetailId,
          )),
      );

      // Bail out if the user picked a different rental invoice while this was in flight.
      if (ref.current.getState().rentalInvoice?.value !== rentalInvoiceValue.value) {
        return;
      }

      const isPaymentMade = paymentDetails.some(
        (paymentDetail) =>
          paymentDetail?.paymentStatus && paymentDetail.paymentStatus !== "Pending",
      );

      if (isPaymentMade) {
        toast.error("Cancel Payment Receipt first");
        ref.current.setFormValue("rentalInvoice", null);
        ref.current.setFormState(emptyInvoiceSelection);
        return;
      }

      ref.current.setFormState(formatRentalInvoiceDetail(rentalInvoice));
    } catch (error) {
      toast.error("Could not validate the selected rental invoice, please try again");
      ref.current.setFormValue("rentalInvoice", null);
      ref.current.setFormState(emptyInvoiceSelection);
    }
  };

  const applyGeneralInvoice = async (generalInvoiceValue) => {
    try {
      const generalInvoice = await getDynamicObjectRecordById(
        dynamicObjectMap.get("GeneralInvoiceObjectName"),
        generalInvoiceValue.value,
      );

      // Bail out if the user picked a different general invoice while this was in flight.
      if (ref.current.getState().generalInvoice?.value !== generalInvoiceValue.value) {
        return;
      }

      if (generalInvoice?.status === "Reversed") {
        toast.error("Cannot select a reversed general invoice");
        ref.current.setFormValue("generalInvoice", null);
        ref.current.setFormState(emptyInvoiceSelection);
        return;
      }

      if (["Partial", "Paid"].includes(generalInvoice?.status)) {
        toast.error("Cancel General Receipt first");
        ref.current.setFormValue("generalInvoice", null);
        ref.current.setFormState(emptyInvoiceSelection);
        return;
      }

      ref.current.setFormState(formatGeneralInvoiceDetail(generalInvoice));
    } catch (error) {
      toast.error("Could not validate the selected general invoice, please try again");
      ref.current.setFormValue("generalInvoice", null);
      ref.current.setFormState(emptyInvoiceSelection);
    }
  };

  const updateRentalDetail = (key, value) => {
    const formState = ref.current.getState();
    formState[key] = value;
    const { rentalDetail, amountOfTax = defaultAmountOfTax } = formState;

    let hasExceededLoadedTotal = false;

    const updatedRentalDetail = (rentalDetail || []).map((rent) => {
      let { amount } = rent;
      const { tax } = rent;
      let taxAmount = 0;

      if (tax?.value && amountOfTax) {
        const parameters = {
          amount,
          amountOfTax,
          tax,
          taxAmount: "",
        };

        const { taxAmount: calculatedTaxAmount, principalAmount } = calculateTax(parameters);

        amount = principalAmount;
        taxAmount = calculatedTaxAmount;
      }

      const rentalDetailItem = {
        ...rent,
        amount,
        tax,
        taxAmount,
        totalAmount: formatDecimalValues(Number(amount) + Number(taxAmount)),
      };

      if (amount) {
        if (rentalDetailItem.rentAmount) rentalDetailItem.rentAmount = amount;
        if (rentalDetailItem.utilityAmount) rentalDetailItem.utilityAmount = amount;
        if (rentalDetailItem.securityDepositAmount) rentalDetailItem.securityDepositAmount = amount;
        if (rentalDetailItem.adminChargesAmount) rentalDetailItem.adminChargesAmount = amount;
        if (rentalDetailItem.generalInvoiceAmount) rentalDetailItem.generalInvoiceAmount = amount;
      }

      if (exceedsLoadedTotal(rentalDetailItem)) {
        hasExceededLoadedTotal = true;
        return { ...rentalDetailItem, ...rentalDetailItem.loadedAmounts };
      }

      return rentalDetailItem;
    });

    if (hasExceededLoadedTotal) {
      toast.error("Total amount cannot exceed the invoice line total amount");
    }

    const totalData = calculateTotalAndTax({
      rentalDetail: updatedRentalDetail,
    });

    ref.current.setFormState({
      rentalDetail: updatedRentalDetail,
      ...totalData,
    });
  };

  const onStateChange = (key, value) => {
    switch (key) {
      case "tenant":
      case "invoiceType":
        ref.current.setFormState({
          rentalInvoice: null,
          generalInvoice: null,
          ...emptyInvoiceSelection,
        });
        break;

      case "rentalInvoice":
        if (!value) {
          ref.current.setFormState(emptyInvoiceSelection);
          break;
        }
        applyRentalInvoice(value);
        break;

      case "generalInvoice":
        if (!value) {
          ref.current.setFormState(emptyInvoiceSelection);
          break;
        }
        applyGeneralInvoice(value);
        break;

      case "rentalDetail":
        updateRentalDetail(key, value);
        break;

      default:
        break;
    }
  };

  const onChildStateChange = ({
    index,
    key,
    value,
    parentField,
    parentFieldType,
  }) => {
    const formState = ref.current.getState();
    const stateKey = `${parentField}${parentFieldType}`;
    let parentFieldState = formState[stateKey] ?? {};

    if (index > -1) {
      parentFieldState = formState[parentField][index];
    }

    if (parentField === "rentalDetail") {
      switch (key) {
        case "amount":
        case "tax": {
          parentFieldState[key] = value;
          const { amount, tax } = parentFieldState;
          const { amountOfTax = defaultAmountOfTax } = formState;

          if (tax?.value && amountOfTax) {
            const parameters = {
              amount,
              amountOfTax,
              tax,
              taxAmount: "",
            };
            const { taxAmount, principalAmount } = calculateTax(parameters);
            parentFieldState.taxAmount = taxAmount;
            parentFieldState.totalAmount = formatDecimalValues(
              Number(principalAmount) + Number(taxAmount)
            );
          } else {
            parentFieldState.taxAmount = 0;
            parentFieldState.totalAmount = formatDecimalValues(Number(amount) || 0);
          }
          break;
        }

        default:
          break;
      }
    }
  };

  return (
    <BoxedContent>
      <DynamicFormContainer
        ref={ref}
        objectName={dynamicObjectMap.get("CreditNoteObjectName")}
        showHeader
        showLinkedViews
        onStateChange={onStateChange}
        onChildStateChange={onChildStateChange}
      />
    </BoxedContent>
  );
}

export default CreditNoteForm;
